// components/CreerAnnonce.js
import React, { useState, useRef } from 'react';
import { WILAYAS_ALPHA } from '../utils/wilayas';
import { useI18n } from '../i18n/I18nContext';
import { useCurrency } from '../utils/currency';
import './CreerAnnonce.css';

const DOCS_IDS = ['acte', 'identite', 'cadastre', 'prix_reel', 'exactitude'];

function CreerAnnonce({ onCreate, onRetour }) {
  const { t } = useI18n();
  const { rates } = useCurrency();

  // Devise de saisie : 'DZD' | 'EUR_OFFICIEL' | 'EUR_PARALLELE'
  const [prixDevise, setPrixDevise] = useState('DZD');
  const DOCS_CHECKLIST = DOCS_IDS.map((id, i) => ({ id, label: t.create.docsChecklist[i] }));
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    prix: '',
    wilaya: '',
    commune: '',
    type: 'Appartement',
    surface: '',
    chambres: '',
    sallesEau: '',
    etage: '',
    nbEtages: '',
    anneeConstruction: '',
    etat: '',
    parking: false,
    balcon: false,
    ascenseur: false,
    meuble: false,
    gardien: false,
    orientation: '',
    telephone: '',
    email: '',
  });

  const [photos, setPhotos] = useState([]); // Array of File objects
  const [photosPreviews, setPhotosPreviews] = useState([]);
  const [video, setVideo] = useState(null);
  const [videoName, setVideoName] = useState('');
  const [docsConfirmed, setDocsConfirmed] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const photosRef = useRef();
  const videoRef = useRef();

  // Communes filtrées selon la wilaya sélectionnée
  const communesList = formData.wilaya
    ? (WILAYAS_ALPHA.find(w => w.nom === formData.wilaya)?.communes || []).slice().sort((a, b) => a.localeCompare(b, 'fr'))
    : [];

  const getProgress = () => {
    const fields = ['titre', 'description', 'prix', 'wilaya', 'commune', 'surface', 'chambres', 'telephone'];
    const filled = fields.filter(f => formData[f] && formData[f].toString().trim() !== '').length;
    const docsOk = Object.values(docsConfirmed).filter(Boolean).length;
    return Math.round(((filled + (docsOk >= DOCS_CHECKLIST.length ? 1 : 0)) / (fields.length + 1)) * 100);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const update = { [name]: type === 'checkbox' ? checked : value };
    if (name === 'wilaya') update.commune = '';
    setFormData(prev => ({ ...prev, ...update }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files).slice(0, 10);
    setPhotos(files);
    const previews = files.map(f => URL.createObjectURL(f));
    setPhotosPreviews(previews);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
    setPhotosPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handleVideo = (e) => {
    const file = e.target.files[0];
    if (file) { setVideo(file); setVideoName(file.name); }
  };

  const toggleDoc = (id) => {
    setDocsConfirmed(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const allDocsConfirmed = DOCS_CHECKLIST.every(d => docsConfirmed[d.id]);

  const validerForm = () => {
    const newErrors = {};
    if (!formData.titre.trim()) newErrors.titre = t.common.error;
    if (!formData.prix || isNaN(formData.prix)) newErrors.prix = t.common.error;
    if (!formData.wilaya) newErrors.wilaya = t.common.error;
    if (!formData.commune) newErrors.commune = t.common.error;
    if (formData.telephone && !/^[0-9\s\-\+]+$/.test(formData.telephone)) newErrors.telephone = t.common.error;
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t.common.error;
    if (!allDocsConfirmed) newErrors.docs = t.create.docsNote;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Calcule le prix en DZD selon la devise choisie
  const getPrixDZD = () => {
    const val = Number(formData.prix);
    if (isNaN(val) || val === 0) return 0;
    if (prixDevise === 'EUR_OFFICIEL') return Math.round(val * rates.officiel);
    if (prixDevise === 'EUR_PARALLELE') return Math.round(val * rates.parallele);
    return val; // DZD
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validerForm()) return;
    setLoading(true);
    try {
      const payload = {
        ...formData,
        prix: getPrixDZD(),
        quartier: `${formData.commune}, ${formData.wilaya}`,
      };
      await onCreate(payload);
      // Reset
      setFormData({ titre: '', description: '', prix: '', wilaya: '', commune: '', type: 'Appartement', surface: '', chambres: '', sallesEau: '', etage: '', nbEtages: '', anneeConstruction: '', etat: '', parking: false, balcon: false, ascenseur: false, meuble: false, gardien: false, orientation: '', telephone: '', email: '' });
      setPhotos([]); setPhotosPreviews([]); setVideo(null); setVideoName(''); setDocsConfirmed({}); setPrixDevise('DZD');
    } catch (err) {
      alert(t.create.errorCreating);
    } finally {
      setLoading(false);
    }
  };

  const progress = getProgress();

  return (
    <div className="creer-annonce-container">
      <button className="btn btn-back" onClick={onRetour}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        {t.create.back}
      </button>

      <div className="creer-header">
        <h1>{t.create.title}</h1>
        <p>{t.create.subtitle}</p>
      </div>

      <div className="progress-bar-container">
        <div className="progress-bar-info">
          <span>{t.create.progress}</span>
          <span className="progress-percentage">{progress}%</span>
        </div>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: `${progress}%` }}/>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="form-annonce">

        {/* SECTION 1: INFOS DE BASE */}
        <fieldset>
          <legend>{t.create.basicInfo}</legend>

          <div className="form-group">
            <label htmlFor="titre">{t.create.titleLabel} <span className="required">*</span></label>
            <input id="titre" type="text" name="titre" value={formData.titre} onChange={handleChange}
              placeholder={t.create.titlePlaceholder} className={errors.titre ? 'has-error' : ''}/>
            {errors.titre && <span className="error-msg">{errors.titre}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description">{t.create.description}</label>
            <textarea id="description" name="description" value={formData.description} onChange={handleChange}
              placeholder={t.create.descPlaceholder} rows="5"/>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">{t.create.typeLabel}</label>
              <select id="type" name="type" value={formData.type} onChange={handleChange}>
                <option value="Appartement">{t.annonce.types.appartement}</option>
                <option value="Villa">{t.annonce.types.villa}</option>
                <option value="Terrain">{t.annonce.types.terrain}</option>
                <option value="Duplex">{t.annonce.types.duplex}</option>
                <option value="Studio">{t.annonce.types.studio}</option>
                <option value="Local commercial">{t.annonce.types.local}</option>
              </select>
            </div>
          </div>
        </fieldset>

        {/* SECTION 2: LOCALISATION */}
        <fieldset>
          <legend>{t.create.location}</legend>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="wilaya">{t.search.wilaya} <span className="required">*</span></label>
              <select id="wilaya" name="wilaya" value={formData.wilaya} onChange={handleChange}
                className={errors.wilaya ? 'has-error' : ''}>
                <option value="">{t.create.selectWilaya}</option>
                {WILAYAS_ALPHA.map(w => (
                  <option key={w.code} value={w.nom}>{w.nom} ({w.code})</option>
                ))}
              </select>
              {errors.wilaya && <span className="error-msg">{errors.wilaya}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="commune">{t.search.commune} <span className="required">*</span></label>
              <select id="commune" name="commune" value={formData.commune} onChange={handleChange}
                className={errors.commune ? 'has-error' : ''} disabled={!formData.wilaya}>
                <option value="">{formData.wilaya ? t.create.selectCommune : t.create.chooseWilayaFirst}</option>
                {communesList.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {errors.commune && <span className="error-msg">{errors.commune}</span>}
            </div>
          </div>
        </fieldset>

        {/* SECTION 3: PRIX ET SURFACE */}
        <fieldset>
          <legend>{t.create.priceAndSize}</legend>

          <div className="form-group">
            <label htmlFor="prix">{t.create.priceLabel} <span className="required">*</span></label>

            {/* Toggle devise */}
            <div className="prix-devise-toggle">
              {[
                { key: 'DZD',          label: 'DZD',              sub: 'Dinar algérien' },
                { key: 'EUR_OFFICIEL', label: '€ Officiel',       sub: `1€ = ${Math.round(rates.officiel)} DZD` },
                { key: 'EUR_PARALLELE',label: '€ Square',         sub: `1€ = ${Math.round(rates.parallele)} DZD` },
              ].map(d => (
                <button
                  key={d.key}
                  type="button"
                  className={`prix-devise-btn${prixDevise === d.key ? ' active' : ''}`}
                  onClick={() => setPrixDevise(d.key)}
                >
                  <span className="devise-label">{d.label}</span>
                  <span className="devise-sub">{d.sub}</span>
                </button>
              ))}
            </div>

            <div className="prix-input-wrap">
              <input
                id="prix"
                type="number"
                name="prix"
                value={formData.prix}
                onChange={handleChange}
                placeholder={prixDevise === 'DZD' ? '5 000 000' : '20 000'}
                className={errors.prix ? 'has-error' : ''}
              />
              <span className="prix-input-suffix">
                {prixDevise === 'DZD' ? 'DZD' : '€'}
              </span>
            </div>
            {errors.prix && <span className="error-msg">{errors.prix}</span>}

            {/* Hint conversion */}
            {formData.prix && !isNaN(formData.prix) && Number(formData.prix) > 0 && (
              <span className="prix-hint">
                {prixDevise === 'DZD' ? (
                  <>
                    ≈ <strong>{Math.round(Number(formData.prix) / rates.parallele).toLocaleString('fr-FR')} €</strong> (square) &nbsp;·&nbsp; <strong>{Math.round(Number(formData.prix) / rates.officiel).toLocaleString('fr-FR')} €</strong> (officiel)
                  </>
                ) : (
                  <>
                    = <strong>{getPrixDZD().toLocaleString('fr-DZ')} DZD</strong> {prixDevise === 'EUR_OFFICIEL' ? '(taux officiel)' : '(taux square)'}
                  </>
                )}
              </span>
            )}
          </div>

          <div className="form-row form-row-3">
            <div className="form-group">
              <label htmlFor="surface">{t.create.surfaceLabel}</label>
              <input id="surface" type="number" name="surface" value={formData.surface} onChange={handleChange} placeholder="120"/>
            </div>
            <div className="form-group">
              <label htmlFor="chambres">{t.create.roomsLabel}</label>
              <input id="chambres" type="number" name="chambres" value={formData.chambres} onChange={handleChange} placeholder="3"/>
            </div>
            <div className="form-group">
              <label htmlFor="sallesEau">{t.create.bathLabel}</label>
              <input id="sallesEau" type="number" name="sallesEau" value={formData.sallesEau} onChange={handleChange} placeholder="2"/>
            </div>
          </div>

          {/* Étage */}
          {formData.type !== 'Terrain' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="etage">Étage</label>
                <input id="etage" type="number" name="etage" value={formData.etage} onChange={handleChange} placeholder="2" min="0"/>
              </div>
              <div className="form-group">
                <label htmlFor="nbEtages">Nombre d'étages total</label>
                <input id="nbEtages" type="number" name="nbEtages" value={formData.nbEtages} onChange={handleChange} placeholder="5" min="1"/>
              </div>
            </div>
          )}

          {/* Année + État */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="anneeConstruction">Année de construction</label>
              <input id="anneeConstruction" type="number" name="anneeConstruction" value={formData.anneeConstruction} onChange={handleChange} placeholder="2010" min="1900" max={new Date().getFullYear()}/>
            </div>
            <div className="form-group">
              <label htmlFor="etat">État du bien</label>
              <select id="etat" name="etat" value={formData.etat} onChange={handleChange}>
                <option value="">— Sélectionner —</option>
                <option value="neuf">Neuf</option>
                <option value="bon_etat">Bon état</option>
                <option value="a_renover">À rénover</option>
              </select>
            </div>
          </div>

          {/* Orientation */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="orientation">Orientation</label>
              <select id="orientation" name="orientation" value={formData.orientation} onChange={handleChange}>
                <option value="">— Sélectionner —</option>
                <option value="nord">Nord</option>
                <option value="sud">Sud</option>
                <option value="est">Est</option>
                <option value="ouest">Ouest</option>
              </select>
            </div>
          </div>

          {/* Équipements */}
          <div className="form-group">
            <label>Équipements</label>
            <div className="equipements-grid">
              {[
                { name: 'parking',   label: '🚗 Parking / Garage' },
                { name: 'balcon',    label: '🌿 Balcon / Terrasse' },
                { name: 'ascenseur', label: '🛗 Ascenseur' },
                { name: 'meuble',    label: '🛋️ Meublé' },
                { name: 'gardien',   label: '🔒 Gardien' },
              ].map(eq => (
                <label key={eq.name} className={`equipement-item ${formData[eq.name] ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    name={eq.name}
                    checked={!!formData[eq.name]}
                    onChange={handleChange}
                  />
                  <span>{eq.label}</span>
                </label>
              ))}
            </div>
          </div>
        </fieldset>

        {/* SECTION 4: MÉDIAS */}
        <fieldset>
          <legend>{t.create.mediaSection}</legend>
          <p className="fieldset-note">{t.create.mediaNote}</p>

          {/* Photos */}
          <div className="form-group">
            <label>{t.create.photos}</label>
            <div className="upload-zone" onClick={() => photosRef.current.click()}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              <span>{t.create.addPhotos}</span>
              <span className="upload-hint">{t.create.photoHint}</span>
            </div>
            <input ref={photosRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePhotos}/>
            {photosPreviews.length > 0 && (
              <div className="photos-preview-grid">
                {photosPreviews.map((src, i) => (
                  <div key={i} className="photo-preview-item">
                    <img src={src} alt={`Photo ${i+1}`}/>
                    <button type="button" className="photo-remove-btn" onClick={() => removePhoto(i)}>×</button>
                    {i === 0 && <span className="photo-main-badge">{t.create.mainPhoto}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Vidéo */}
          <div className="form-group">
            <label>{t.create.videoLabel} <span className="optional">{t.create.optionalLabel}</span></label>
            <div className="upload-zone upload-zone-video" onClick={() => videoRef.current.click()}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polygon points="23 7 16 12 23 17 23 7"/>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
              </svg>
              {videoName ? (
                <span className="upload-video-name">{videoName}</span>
              ) : (
                <>
                  <span>{t.create.addVideo}</span>
                  <span className="upload-hint">{t.create.videoHint}</span>
                </>
              )}
            </div>
            <input ref={videoRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleVideo}/>
          </div>
        </fieldset>

        {/* SECTION 5: CONTACT */}
        <fieldset>
          <legend>{t.create.contactSection}</legend>
          <p className="fieldset-note">{t.create.contactNote}</p>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="telephone">{t.create.phoneLabel}</label>
              <input id="telephone" type="tel" name="telephone" value={formData.telephone} onChange={handleChange}
                placeholder="0555 123 456" className={errors.telephone ? 'has-error' : ''}/>
              {errors.telephone && <span className="error-msg">{errors.telephone}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="email">{t.create.emailLabel}</label>
              <input id="email" type="email" name="email" value={formData.email} onChange={handleChange}
                placeholder="contact@exemple.com" className={errors.email ? 'has-error' : ''}/>
              {errors.email && <span className="error-msg">{errors.email}</span>}
            </div>
          </div>
        </fieldset>

        {/* SECTION 6: DOCUMENTS & CONFIRMATION */}
        <fieldset className={`fieldset-docs ${errors.docs ? 'fieldset-error' : ''}`}>
          <legend>{t.create.docsSection}</legend>
          <p className="fieldset-note">{t.create.docsNote}</p>

          <div className="docs-checklist">
            {DOCS_CHECKLIST.map(doc => (
              <label key={doc.id} className={`doc-check-item ${docsConfirmed[doc.id] ? 'checked' : ''}`}>
                <input
                  type="checkbox"
                  checked={!!docsConfirmed[doc.id]}
                  onChange={() => toggleDoc(doc.id)}
                />
                <span className="doc-check-box">
                  {docsConfirmed[doc.id] && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </span>
                <span className="doc-check-label">{doc.label}</span>
              </label>
            ))}
          </div>
          {errors.docs && <span className="error-msg">{errors.docs}</span>}

          <div className="docs-legal-note">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{t.create.docsLegal}</span>
          </div>
        </fieldset>

        {/* BOUTONS */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary btn-submit" disabled={loading || !allDocsConfirmed}>
            {loading ? (
              <><div className="btn-spinner"/>{t.create.submitting}</>
            ) : (
              <><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>{t.create.submit}</>
            )}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onRetour}>{t.create.cancel}</button>
        </div>

        <div className="form-trust-note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
          <span>{t.create.trustNote}</span>
        </div>
      </form>
    </div>
  );
}

export default CreerAnnonce;
